import { updateShippingAddress } from "@/api/rest/authenticated/Address";
import { ShippingAddress } from "@/components/Checkout/types";
import { StoreError } from "@/components/StoreComponents/StoreError";
import { useState } from "react";
import { isAddressEqual } from "../../../../../CheckoutSummary/CheckoutEditShippingForm";
import { useCheckoutUI } from "../../../../CheckoutUIProvider";
import { CheckoutIntent } from "../../../../types/CheckoutIntent";
import MobileCheckoutEditShippingForm from "./MobileCheckoutEditShippingForm";
import { Skeleton } from "@mui/material";

export const MobileShippingMethodForm = () => {
  const [machineState, send] = useCheckoutUI();

  const [addressError, setAddressError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { checkoutIntent } = machineState.context;

  const handleValidAddress = async ({
    shippingInput,
    skipValidation = false,
  }: {
    shippingInput: CheckoutIntent["shippingAddress"];
    skipValidation?: boolean;
  }) => {
    if (!shippingInput || !checkoutIntent?.checkoutIntentId) return;

    const { checkoutIntent: updatedCheckoutIntent, status } =
      await updateShippingAddress({
        shippingAddress: shippingInput,
        checkoutIntentId: checkoutIntent.checkoutIntentId,
        skipValidation,
      });

    if (status === "success") {
      // First update checkout intent data
      send({
        type: "SET_CHECKOUT_INTENT",
        data: { checkoutIntent: updatedCheckoutIntent },
      });

      // Mark shipping section as valid and completed
      send({ type: "SET_IS_VALID_ADDRESS", data: true });
    } else {
      setAddressError(
        "An error occurred while updating your address. Please try again."
      );
    }
  };

  const handleSaveAddress = async (event: {
    address: Partial<ShippingAddress>;
    skipValidation?: boolean;
  }) => {
    // Clear previous errors
    setAddressError(null);

    const { address, skipValidation } = event;

    const { firstName, lastName, address1, city, state, zipCode } =
      address || {};

    // Basic form validation
    if (!address1 || !city || !state || !zipCode || !firstName || !lastName) {
      setAddressError("Please fill in all required address fields");
      return;
    }

    try {
      setLoading(true);
      send({ type: "SET_IS_VALID_ADDRESS", data: true });

      handleValidAddress({
        shippingInput: address as ShippingAddress,
        skipValidation,
      });
    } catch (error) {
      setAddressError(
        "An error occurred while validating your address. Please try again."
      );
      console.error("Address validation error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAddress = async (
    data: Partial<CheckoutIntent["shippingAddress"]>
  ) => {
    setLoading(true);
    if (
      checkoutIntent &&
      !isAddressEqual(checkoutIntent?.shippingAddress, data as ShippingAddress)
    ) {
      // Clear error when user updates any field
      setAddressError(null);
      send({ type: "RESET_IS_VALID_ADDRESS" });
      send({ type: "SET_SHIPPING", data });
      await handleValidAddress({
        shippingInput: data as ShippingAddress,
        skipValidation: !(data as ShippingAddress).isValid,
      });
    }
    setLoading(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <Skeleton
            variant="rectangular"
            height={25}
            sx={{ backgroundColor: "#F6F8FA" }}
          />
          <Skeleton
            variant="rectangular"
            height={25}
            sx={{ backgroundColor: "#F6F8FA" }}
          />
        </div>
      ) : (
        <MobileCheckoutEditShippingForm
          loading={loading}
          availableAddresses={
            (checkoutIntent?.availableShippingAddresses ??
              []) as ShippingAddress[]
          }
          handleSaveShippingAddress={handleSaveAddress}
          handleSelectShippingAddress={handleSelectAddress}
          currentAddress={
            checkoutIntent?.shippingAddress
              ? ({
                  ...checkoutIntent?.shippingAddress,
                  extendedAttributes: {
                    isSelected: true,
                  },
                } as ShippingAddress)
              : undefined
          }
          onCancel={() => {
            send({ type: "CLOSE", data: { section: "shipping" } });
          }}
        />
      )}
      {addressError && <StoreError errorMessage={addressError} />}
    </div>
  );
};
